// shared/src/hydraulic/scenarios/resolver.ts
import type { HydraulicSystem } from '../system'
import type { ScenarioNode, Override } from './schema'

/** Ancestor chain from root → target (inclusive). Empty if id not found. */
export function getAncestorChain(nodes: ScenarioNode[], scenarioId: string): ScenarioNode[] {
  const byId = new Map(nodes.map((n) => [n.id, n]))
  const chain: ScenarioNode[] = []
  let cur = byId.get(scenarioId)
  while (cur) {
    chain.push(cur)
    cur = cur.parent_id === null ? undefined : byId.get(cur.parent_id)
  }
  return chain.reverse()
}

/** Group overrides by target element id, keeping last-write-wins within a node
 * and child-over-ancestor across the chain (chain is already root→leaf). */
function collectOverrides(chain: ScenarioNode[]): {
  vertex: Map<string, Override[]>
  edge: Map<string, Override[]>
} {
  const vertex = new Map<string, Override[]>()
  const edge = new Map<string, Override[]>()
  for (const node of chain) {
    for (const ov of node.overrides) {
      const map = ov.target === 'vertex' ? vertex : edge
      const list = map.get(ov.id) ?? []
      // Replace any earlier override of the same field, then push this one last.
      const filtered = list.filter((o) => o.field !== ov.field)
      filtered.push(ov)
      map.set(ov.id, filtered)
    }
  }
  return { vertex, edge }
}

/** Apply a single override to a cloned element (top-level set; pipe_spec is two keys). */
function applyOverride(el: Record<string, unknown>, ov: Override): void {
  if (ov.field === 'pipe_spec') {
    el['pipe_specification_id'] = ov.value.pipe_specification_id
    el['pipe_size_id'] = ov.value.pipe_size_id
  } else {
    el[ov.field] = ov.value
  }
}

/**
 * Materialize a full HydraulicSystem for `scenarioId`. Base values live on
 * system.vertices/edges; child nodes layer sparse overrides root→leaf.
 *
 * Performance: when the active scenario is the root (no overrides in the
 * chain), the original `system` reference is returned unchanged. Otherwise only
 * the overridden elements are structuredClone'd (preserving `undefined`
 * optionals); untouched elements are shared by reference. Base `.calc` is
 * stripped from cloned elements so a non-Base view never shows Base results.
 *
 * Passing a `scenarioId` not present in `system.scenarios` throws: callers must
 * pass a valid id (upstream schema/closure guarantees `active_id` exists).
 */
export function resolveScenario(system: HydraulicSystem, scenarioId: string): HydraulicSystem {
  const scenarios = system.scenarios
  if (!scenarios) return system

  if (!scenarios.nodes.some((n) => n.id === scenarioId)) {
    throw new Error(`resolveScenario: scenario '${scenarioId}' is not in system.scenarios`)
  }

  const chain = getAncestorChain(scenarios.nodes, scenarioId)
  const { vertex, edge } = collectOverrides(chain)
  if (vertex.size === 0 && edge.size === 0) return system // root / no-delta short-circuit

  const vertices = system.vertices.map((v) => {
    const ovs = vertex.get(v.id)
    if (!ovs) return v
    const clone = structuredClone(v) as Record<string, unknown>
    delete clone['calc']
    for (const ov of ovs) applyOverride(clone, ov)
    return clone as unknown as HydraulicSystem['vertices'][number]
  })

  const edges = system.edges.map((e) => {
    const ovs = edge.get(e.id)
    if (!ovs) return e
    const clone = structuredClone(e) as Record<string, unknown>
    delete clone['calc']
    for (const ov of ovs) applyOverride(clone, ov)
    return clone as unknown as HydraulicSystem['edges'][number]
  })

  return { ...system, vertices, edges }
}
